"use client"

/**
 * AHD-6470 — membership-vs-one-off comparison for /go/bloodtest.
 *
 * Two cards side by side, ported from the booking-flow "Blood tracking"
 * layout (ahead-booking-new BloodTrackingLayout + MembershipFrequencyCard):
 *
 *   left  → Ahead membership (recommended, lavender-tinted, "Most popular",
 *           annual price, full 6-row checklist all green)
 *   right → One-off blood test (white, "Essentials", live blood price, same 6
 *           rows but only the first 3 checked — the asymmetry IS the comparison)
 *
 * Each card has its own CTA: membership → Ahead Membership sign-up,
 * one-off → booking flow with the blood product preselected.
 *
 * Copy lives in the `go` namespace. Strings whose English is byte-identical to
 * an existing `go.bloodtestCompare*` / `go.membershipHero*` entry reuse that
 * key rather than duplicating a translation across all seven locale files —
 * those reuses are marked inline below.
 *
 * Money never crosses a market (AHD-9744). The one-off amount is the live
 * `prices.blood` with the feed's own currency label, hidden when the feed has
 * no entry. The membership card renders only where the membership booking flow
 * is open (`isBookingOpen(country, "membership")`), and its figures come from
 * MEMBERSHIP_FIGURES_BY_CURRENCY. Elsewhere the one-off card stands alone.
 *
 * This component is gated by the pages that render it, not by the membership
 * flag: /tests/cortisol-blood-test is open to AT, so the Swiss figures that
 * used to be literals here would have printed on /at/de the day AT_ENABLED
 * flipped, long before AT launched membership. Same contract as
 * blood-tracking-comparison.tsx.
 */

import { useEffect, useState } from "react"
import Image from "next/image"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { CheckCircleIcon, XCircleIcon } from "@/components/ui/icons"
import { MembershipPillIcon } from "@/components/ui/new-membership-pill"
import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { isBookingOpen } from "@/i18n/config"
import { buildFallback, fetchPrices, type PriceData } from "@/lib/pricing"
import { formatNumber } from "@/lib/format"
import {
  ADDON_PRICE_KEYS,
  MRI_PACKAGE_PRICE_KEYS,
  maxMemberSaving,
} from "@/lib/pricing/member-prices"

type GoTranslator = ReturnType<typeof useTranslations<"go">>

/* The key unions stay narrow (rather than the whole `go` keyspace) so `t(key)`
   still knows none of these messages take ICU values — widening to every `go`
   key would make the compiler demand the union of every placeholder in the
   namespace at each call site. */
type BenefitRowKey =
  | "bloodMembershipRowBloodTest"
  | "bloodMembershipRowReport"
  | "bloodMembershipRowAddAnytime"
  | "bloodMembershipRowFourTests"
  | "bloodMembershipRowSupport"
  | "bloodMembershipRowMemberPricing"

type DetailedFeatureKey =
  | "bloodtestCompareBenefitAdvancedTest"
  | "bloodtestCompareBenefitReport"
  | "bloodtestCompareBenefitFourTests"
  | "bloodtestCompareBenefitPrevention"
  | "bloodtestCompareBenefitFollowup"
  | "bloodtestCompareBenefitUpload"
  | "bloodMembershipFeatureMriDiscount"
  | "bloodMembershipFeatureAddonDiscount"

type FeatureTagKey = "bloodMembershipTagIncluded" | "bloodMembershipTagFlexible"

/* 6 comparison rows. The first three are included in the one-off too
   (green check on both); the last three are membership-only (soft X on
   the one-off). Order + copy mirror MEMBERSHIP_BENEFIT_ROWS in the
   booking flow. */
const BENEFIT_ROW_KEYS: { key: BenefitRowKey; inOneOff: boolean }[] = [
  { key: "bloodMembershipRowBloodTest", inOneOff: true },
  { key: "bloodMembershipRowReport", inOneOff: true },
  { key: "bloodMembershipRowAddAnytime", inOneOff: true },
  { key: "bloodMembershipRowFourTests", inOneOff: false },
  { key: "bloodMembershipRowSupport", inOneOff: false },
  { key: "bloodMembershipRowMemberPricing", inOneOff: false },
]

/**
 * Membership figures per market currency. Membership is CH-only today, so CHF
 * is the only entry. A currency with no entry gets no membership money at all
 * rather than the Swiss figure under another label; adding one is a deliberate
 * act with a real price behind it. See MEMBERSHIP_PRICING_BY_CURRENCY in
 * blood-tracking-comparison.tsx for the same contract.
 */
const MEMBERSHIP_FIGURES_BY_CURRENCY: Record<
  string,
  | {
      /** Annual membership price. */
      annual: number
    }
  | undefined
> = {
  CHF: { annual: 299 },
}

/** Member savings shown on the detailed card. Computed from the live Stripe
 *  feed only (regular − member); null hides the figure rather than printing a
 *  literal the price list does not back (AHD-13080). */
type MemberSavings = {
  /** Largest saving across the three MRI packages. */
  mriDiscount: number | null
  /** Largest saving across the add-ons. */
  addonDiscount: number | null
  /** Headline "save up to": one MRI package plus one add-on, each at its
   *  largest saving. Null unless both are live. */
  savings: number | null
}

function memberSavingsFromFeed(
  prices: PriceData | null | undefined
): MemberSavings {
  const mriDiscount = maxMemberSaving(prices?.prices, MRI_PACKAGE_PRICE_KEYS)
  const addonDiscount = maxMemberSaving(prices?.prices, ADDON_PRICE_KEYS)
  return {
    mriDiscount,
    addonDiscount,
    savings:
      mriDiscount != null && addonDiscount != null
        ? mriDiscount + addonDiscount
        : null,
  }
}

type MembershipFigures = NonNullable<
  (typeof MEMBERSHIP_FIGURES_BY_CURRENCY)[string]
> &
  MemberSavings

/* Shared card shadow (from the booking one-off PackageCard). Applied to
   both cards so they read as one matched pair. */
const CARD_SHADOW = "shadow-[0px_3px_16px_0px_rgba(106,133,161,0.05)]"

/* `tagKey` is a translated chip; `tagAmount` picks a live member-saving figure
   (see memberSavingsFromFeed), printed with the market's currency.
   Six of the eight labels are reused from the booking-comparison benefit
   catalogue, whose English is identical. */
const DETAILED_MEMBERSHIP_FEATURES: {
  key: DetailedFeatureKey
  tagKey?: FeatureTagKey
  tagAmount?: "mriDiscount" | "addonDiscount"
}[] = [
  {
    key: "bloodtestCompareBenefitAdvancedTest",
    tagKey: "bloodMembershipTagIncluded",
  },
  { key: "bloodtestCompareBenefitReport" },
  {
    key: "bloodtestCompareBenefitFourTests",
    tagKey: "bloodMembershipTagFlexible",
  },
  { key: "bloodMembershipFeatureMriDiscount", tagAmount: "mriDiscount" },
  { key: "bloodMembershipFeatureAddonDiscount", tagAmount: "addonDiscount" },
  { key: "bloodtestCompareBenefitPrevention" },
  { key: "bloodtestCompareBenefitFollowup" },
  { key: "bloodtestCompareBenefitUpload" },
]

function DetailedFeatureRow({ label, tag }: { label: string; tag?: string }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <CheckCircleIcon size={20} className="flex-shrink-0" />
      <span className="type-body-md min-w-0 flex-1 text-neutral-900">
        {label}
      </span>
      {tag && (
        <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block shrink-0 rounded-full px-5 py-1 font-medium whitespace-nowrap normal-case">
          {tag}
        </span>
      )}
    </div>
  )
}

function MembershipCardDetailed({
  badgeLabel,
  t,
  currency,
  figures,
}: {
  badgeLabel: string
  t: GoTranslator
  currency: string
  figures: MembershipFigures
}) {
  const { code: country } = useCountry()
  return (
    <div
      className={cn(
        "pill-magic-stroke pill-magic-stroke-always relative flex flex-col rounded-[20px] bg-gradient-to-br from-[#FCFAFE] via-[#FAFBFE] to-[#F7FBFF] p-5 md:p-8",
        CARD_SHADOW
      )}
    >
      <div className="absolute top-0 right-12 z-10 -translate-y-[calc(50%+2px)]">
        <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block rounded-full px-5 py-1 font-medium normal-case">
          {badgeLabel}
        </span>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center gap-3">
          <MembershipPillIcon size={40} />
          <h3 className="type-headline-md text-neutral-900">
            {/* Reused: EN identical to the booking-comparison card title. */}
            {t("bloodtestCompareMembershipTitle")}
          </h3>
        </div>
        <p className="type-body-md mt-2 text-neutral-500">
          {t("bloodMembershipDetailedTagline")}
        </p>
        {figures.savings != null && (
          <div className="bg-deep-green-50 mt-4 self-start rounded-[10px] px-4 py-2.5">
            <p className="type-body-md text-deep-green-700 font-medium">
              {t("bloodMembershipDetailedSavings", {
                price: `${currency} ${formatNumber(figures.savings, country)}`,
              })}
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 border-t border-neutral-200 pt-2">
        {DETAILED_MEMBERSHIP_FEATURES.map((row) => (
          <DetailedFeatureRow
            key={row.key}
            label={t(row.key)}
            tag={
              row.tagKey
                ? t(row.tagKey)
                : row.tagAmount && figures[row.tagAmount] != null
                  ? `-${currency} ${formatNumber(figures[row.tagAmount] as number, country)}`
                  : undefined
            }
          />
        ))}
      </div>

      <div className="mt-6 border-t border-neutral-200 pt-6">
        <p className="text-neutral-900">
          <span className="type-display-sm">
            {currency} {figures.annual}
          </span>
          <span className="type-body-lg text-neutral-500">
            {" "}
            {t("bloodMembershipPerYear")}
          </span>
        </p>
        <p className="type-body-sm mt-1 text-neutral-500">
          {t("bloodMembershipBilling")}
        </p>
      </div>

      <div className="mt-6">
        <BookingCta
          forMembership
          trackingLabel="bloodtest_membership_card_cta"
          trackingLocation="go/bloodtest"
          className="block"
        >
          <Button
            variant="primary"
            className="group w-full"
            iconRight={
              <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
            }
          >
            {t("bloodMembershipCta")}
          </Button>
        </BookingCta>
      </div>
    </div>
  )
}

function BenefitRow({ label, included }: { label: string; included: boolean }) {
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      {included ? (
        <CheckCircleIcon size={20} className="flex-shrink-0" />
      ) : (
        <XCircleIcon size={20} className="flex-shrink-0 text-neutral-300" />
      )}
      <span
        className={cn(
          "type-body-md",
          included ? "text-neutral-900" : "text-neutral-400"
        )}
      >
        {label}
      </span>
    </div>
  )
}

export function BloodMembershipComparison({
  membershipBadge,
  membershipVariant = "default",
}: {
  /** Overrides the translated "Most popular" badge. */
  membershipBadge?: string
  membershipVariant?: "default" | "detailed"
}) {
  const lp = useLocalePath()
  const t = useTranslations("go")
  const badgeLabel = membershipBadge ?? t("bloodMembershipBadge")
  const country = useCountry().code

  // Seeded with the central fallback table so first paint has a price and no
  // per-site literal is needed (empty on pre-launch EUR markets by design).
  const [prices, setPrices] = useState<PriceData | null>(() =>
    buildFallback(country)
  )
  useEffect(() => {
    fetchPrices(country).then(setPrices)
  }, [country])
  const currency = prices?.currency
  const bloodPrice = prices?.prices["blood"]

  // The membership card needs both an open membership flow in this market and
  // an authoritative figure in its currency. Missing either, the one-off card
  // is the whole offer.
  const annual = currency ? MEMBERSHIP_FIGURES_BY_CURRENCY[currency] : undefined
  const figures = annual
    ? { ...annual, ...memberSavingsFromFeed(prices) }
    : undefined
  const membership =
    isBookingOpen(country, "membership") && currency && figures
      ? { currency, figures }
      : null

  return (
    <div className="mx-auto max-w-5xl px-4 md:px-10">
      {/* Both cards are row-span-4 grid-rows-subgrid so header / price / CTA /
          checklist sit on the same baseline across the two columns (same
          alignment trick as the /pricing PricingCard). Alone, the one-off
          card is centred at a single column's width. */}
      <div
        className={cn(
          "grid grid-cols-1 items-start gap-6",
          membership ? "md:grid-cols-2" : "mx-auto max-w-lg"
        )}
      >
        {!membership ? null : membershipVariant === "detailed" ? (
          <MembershipCardDetailed
            badgeLabel={badgeLabel}
            t={t}
            currency={membership.currency}
            figures={membership.figures}
          />
        ) : (
          /* ── Left: Ahead membership (recommended) ── */
          <div
            className={cn(
              "pill-magic-stroke pill-magic-stroke-always relative row-span-4 grid grid-rows-subgrid gap-y-6 rounded-[20px] bg-gradient-to-br from-[#FCFAFE] via-[#FAFBFE] to-[#F7FBFF] p-5 md:p-8",
              CARD_SHADOW
            )}
          >
            {/* "Most popular" badge straddling the top border — same treatment
                as the /pricing Advanced card, so it never collides with the
                product name. */}
            <div className="absolute top-0 right-12 z-10 -translate-y-[calc(50%+2px)]">
              <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block rounded-full px-5 py-1 font-medium normal-case">
                {badgeLabel}
              </span>
            </div>

            {/* Row 1: header */}
            <div className="flex flex-col">
              <div className="flex items-center gap-3">
                <MembershipPillIcon size={40} />
                <h3 className="type-headline-md text-neutral-900">
                  {/* Reused: EN identical to the booking-comparison title. */}
                  {t("bloodtestCompareMembershipTitle")}
                </h3>
              </div>
              <p className="type-body-md mt-2 text-neutral-500">
                {t("bloodMembershipTagline")}
              </p>
              <p className="type-body-lg mt-2 text-neutral-900">
                {t("bloodMembershipDescription")}
              </p>
            </div>

            {/* Row 2: price */}
            <div className="flex items-baseline gap-1">
              <span className="type-body-md text-neutral-500">
                {/* Reused: the shared "from" price prefix. */}
                {t("membershipHeroFromLabel")} {membership.currency}
              </span>
              <span className="type-display-sm text-neutral-900">
                {membership.figures.annual}
              </span>
              <span className="type-body-md text-neutral-500">
                {t("bloodMembershipPerYearCancel")}
              </span>
            </div>

            {/* Row 3: CTA */}
            <div>
              <BookingCta
                forMembership
                trackingLabel="bloodtest_membership_card_cta"
                trackingLocation="go/bloodtest"
                className="block"
              >
                <Button
                  variant="primary"
                  className="group w-full"
                  iconRight={
                    <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                  }
                >
                  {t("bloodMembershipCta")}
                </Button>
              </BookingCta>
            </div>

            {/* Row 4: checklist */}
            <div className="flex flex-col">
              {BENEFIT_ROW_KEYS.map((row) => (
                <BenefitRow key={row.key} label={t(row.key)} included />
              ))}
            </div>
          </div>
        )}

        {/* ── Right: One-off blood test (Advanced) ── */}
        <div
          className={cn(
            "row-span-4 grid grid-rows-subgrid gap-y-6 rounded-[20px] bg-white p-5 md:p-8",
            CARD_SHADOW
          )}
        >
          {/* Row 1: header */}
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <span className="relative size-10 flex-shrink-0 overflow-hidden rounded-full">
                <Image
                  src={blobMediaUrl(
                    "/images/homepage/service-blood-cells.jpeg"
                  )}
                  alt=""
                  fill
                  sizes="40px"
                  className="object-cover"
                />
              </span>
              <h3 className="type-headline-md text-neutral-900">
                {t("bloodMembershipOneOffTitle")}
              </h3>
            </div>
            <p className="type-body-md mt-2 text-neutral-500">
              {t("bloodMembershipOneOffTagline")}
            </p>
            <p className="type-body-lg mt-2 text-neutral-900">
              {t("bloodMembershipOneOffDescription")}
            </p>
          </div>

          {/* Row 2: price. No authoritative blood price (pre-launch EUR
              markets) → no price line, never an invented figure. */}
          <div className="flex items-baseline gap-1">
            {bloodPrice && (
              <>
                <span className="type-body-md text-neutral-500">
                  {currency}
                </span>
                <span className="type-display-sm text-neutral-900">
                  {bloodPrice}
                </span>
              </>
            )}
          </div>

          {/* Row 3: CTA */}
          <div>
            <BookingCta
              productId={PRODUCT_IDS.bloodTest}
              trackingLabel="bloodtest_oneoff_card_cta"
              trackingLocation="go/bloodtest"
              className="block"
            >
              <Button
                variant="primary-alt"
                className="group w-full"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                {/* Reused: EN identical to the booking-comparison CTA. */}
                {t("bloodtestCompareBaselineCta")}
              </Button>
            </BookingCta>
          </div>

          {/* Row 4: checklist */}
          <div className="flex flex-col">
            {/* Without a membership card beside it, the membership-only
                rows compare against nothing — drop them. */}
            {BENEFIT_ROW_KEYS.filter((row) => membership || row.inOneOff).map(
              (row) => (
                <BenefitRow
                  key={row.key}
                  label={t(row.key)}
                  included={row.inOneOff}
                />
              )
            )}
          </div>
        </div>
      </div>

      {membership && (
        <div className="mt-8 flex justify-center">
          <Link href={lp("/membership")}>
            <Button
              variant="outline"
              className="group"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              {t("bloodMembershipLearnMore")}
            </Button>
          </Link>
        </div>
      )}
    </div>
  )
}
